import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMicrosoftAuth } from "../context/MicrosoftAuthContext";
import { Button } from "../components/ui/Button";

// Preload the background image
const preloadImage = (url) => {
  const img = new Image();
  img.src = url;
};

// Preload the optimized background image
preloadImage('/assets/images/loginbg.jpg');

const Login2 = () => {
  const { login, isAuthenticated, isLoading, isInitialized } = useMicrosoftAuth();
  const navigate = useNavigate();
  const [loginInProgress, setLoginInProgress] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleMicrosoftLogin = async () => {
    if (loginInProgress || !isInitialized) {
      return; // Don't allow multiple login attempts or login before initialization
    }

    try {
      setLoginInProgress(true);
      setLoginError(null);
      const success = await login();
      if (success) {
        navigate("/dashboard");
      } else {
        setLoginError("Login failed. Please try again.");
      }
    } catch (err) {
      console.error("Microsoft login error:", err);
      setLoginError(`Login error: ${err.message || "An unexpected error occurred"}`);
    } finally {
      setLoginInProgress(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-button-dark flex items-center justify-center p-4">
      {/* Background Image with 20% opacity */}
      <div className="absolute inset-0 opacity-20 overflow-hidden">
        <img
          src="/assets/images/loginbg.jpg"
          alt="Background"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
        />
      </div>

      {/* Login Card */}
      <div className="relative bg-gray-50 rounded-[15px] px-6 py-8 sm:px-[47px] sm:py-[37px] w-full sm:w-[501px]">
        <div className="flex flex-col gap-[62px] items-center w-full">
          {/* Logo */}
          <img
            src="/assets/icons/logoblack.svg"
            alt="Jamesons Logo"
            className="h-[31px] w-[169px]"
          />

          {/* Content Section */}
          <div className="flex flex-col gap-[72px] items-center w-full">
            {/* Text Section */}
            <div className="flex flex-col gap-[15px] text-center text-login-text w-full">
              <h1 className="text-2xl font-bold m-0">
                Log in to your account
              </h1>
              <p className="text-base font-normal m-0">
                You can access your account using Microsoft. Click on the button below and you will be redirected to microsoft login page
              </p>
            </div>

            {/* Messages and Button Section */}
            <div className="flex flex-col gap-4 w-full">
              {/* Initialization Message */}
              {!isInitialized && !isLoading && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm w-full">
                  Initializing authentication. Please wait...
                </div>
              )}

              {/* Error Message */}
              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm w-full" role="alert">
                  {loginError}
                </div>
              )}

              {/* Login Button */}
              <Button
                variant="dark"
                className="self-center"
                onClick={handleMicrosoftLogin}
                disabled={loginInProgress || isLoading || !isInitialized}
                icon={<img src="/assets/icons/window.svg" alt="" className="size-4 shrink-0" />}
              >
                {loginInProgress || isLoading ? "Loading..." : "Login with Microsoft"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login2;